<template>
  <div class="metrics-rubric-breakdown">
    <div class="metrics-rubric-breakdown__header">
      <div>
        <h3 class="metrics-rubric-breakdown__title heading-xs">
          {{ $t("metrics.rubricBreakdown") }}
        </h3>
        <p class="metrics-rubric-breakdown__subtitle caption-s">
          {{ $t("metrics.averageScorePerCriterion") }}
        </p>
      </div>
      <eg-button
        v-if="rubricId"
        type="secondary"
        @click="$emit('view-rubric', rubricId)"
      >
        {{ $t("metrics.viewRubric") }}
      </eg-button>
    </div>

    <infinity-loading v-if="isLoading && !criteria.length" />

    <div v-else-if="criteria.length" class="metrics-rubric-breakdown__list">
      <div
        v-for="criterion in criteria"
        :key="criterion.criterionId"
        class="metrics-rubric-breakdown__item"
      >
        <div class="metrics-rubric-breakdown__item-header">
          <span class="metrics-rubric-breakdown__criterion-name paragraph-s">
            {{ criterion.criterionName }}
          </span>
          <div class="metrics-rubric-breakdown__item-score">
            <span
              class="metrics-rubric-breakdown__level caption-s"
              :style="{
                color: levelColor(criterion.label),
                backgroundColor: levelBackgroundColor(criterion.label),
              }"
            >
              {{ criterion.label }}
            </span>
            <span class="metrics-rubric-breakdown__score">
              <strong>{{ criterion.avgGrade }}</strong> /
              {{ criterion.maxGrade }}
            </span>
          </div>
        </div>
        <div class="metrics-rubric-breakdown__bar-track">
          <div
            class="metrics-rubric-breakdown__bar-fill"
            :style="{
              width: `${(criterion.avgGrade / criterion.maxGrade) * 100}%`,
              background: levelBarColor(criterion.label),
            }"
          />
        </div>
      </div>
    </div>

    <metrics-empty-state v-else-if="!isLoading" />
  </div>
</template>

<script>
import MetricsEmptyState from "../shared/metrics_empty_state.vue";
import InfinityLoading from "@/components/rubrics/newRubric/components/infinity-loading.vue";

const LEVEL_CONFIG = {
  high: {
    labels: ["Strong", "Proficient", "Excellent"],
    color: "var(--color-text-semantic-onSuccess, #308C4A)",
    barColor: "var(--color-data-chartGreen, #28A745)",
    backgroundColor: "var(--color-surface-semantic-success-light, #F0FAF3)",
  },
  medium: {
    labels: ["Growing", "Developing"],
    color: "var(--colors-secondary-600, #1E88E5)",
    barColor: "var(--color-data-chartPurple, #2196F3)",
    backgroundColor: " var(--colors-secondary-50, #E3F2FD)",
  },
  low: {
    labels: ["Needs Support", "Beginning"],
    color: "var(--color-text-semantic-onError, #DC3545)",
    barColor: "var(--color-data-chartRed, #DC3545)",
    backgroundColor: "var(--color-surface-semantic-error-light, #FDEFEC)",
  },
};

function getLevelConfig(label) {
  const entry = Object.values(LEVEL_CONFIG).find((cfg) =>
    cfg.labels.some((l) => l.toLowerCase() === (label || "").toLowerCase())
  );
  return entry || LEVEL_CONFIG.medium;
}

export default {
  name: "MetricsRubricBreakdown",

  components: { MetricsEmptyState, InfinityLoading },

  props: {
    criteria: {
      type: Array,
      default: () => [],
    },
    rubricId: {
      type: [String, Number],
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    maxPts() {
      return this.criteria[0]?.maxGrade ?? null;
    },
  },

  methods: {
    levelColor(label) {
      return getLevelConfig(label).color;
    },
    levelBackgroundColor(label) {
      return getLevelConfig(label).backgroundColor;
    },
    levelBarColor(label) {
      return getLevelConfig(label).barColor;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../shared/metrics_card";

.metrics-rubric-breakdown {
  @include metrics-card;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  min-width: 0;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  &__title {
    color: var(--color-text-neutral-on-white);
    margin: 0 0 4px;
  }

  &__subtitle {
    font-size: var(--typography-font-size-fs-200);
    color: var(--color-text-neutral-fade);
    margin: 0;
    strong {
      color: var(--color-text-neutral-fade);
      font-size: var(--typography-font-size-fs-400);
      vertical-align: top;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__item-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  &__criterion-name {
    color: var(--color-text-neutral-on-white);
    font-weight: var(--typography-font-weight-fw-600, 600);
  }

  &__item-score {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  &__level {
    padding: 4px 6px;
    font-weight: var(--typography-font-weight-fw-600, 600);
    border-radius: var(--radius-rd-500, 16px);
  }

  &__score {
    font-size: var(--typography-font-size-fs-300);
    font-weight: var(--typography-font-weight-fw-700, 700);
    color: var(--color-text-neutral-secondary, #566370);
    letter-spacing: -0.15px;
    strong {
      color: var(--color-text-neutral-on-white);
      font-weight: var(--typography-font-weight-fw-900, 900);
    }
  }

  &__bar-track {
    height: 8px;
    border-radius: 4px;
    background: var(--color-surface-neutral-container-1, #f6f8fa);
    overflow: hidden;
  }

  &__bar-fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.4s ease;
  }
}
</style>
